import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Map, Placemark, YMaps } from '@pbe/react-yandex-maps';

import { makeStyles,  Box,
  Dialog,
  ListItemText,
  List,
  ListItem,
  Typography,
  styled,
  Button, } from '@material-ui/core';
  
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import CloseIcon from '@material-ui/icons/Close';
import { useIntl } from 'react-intl';
import FilterField from './FilterField';

const useStyles = makeStyles(theme => {
  return {
    input: {
      width: '45%',
      position: 'absolute',
      top: '13px',
      left: '24px',
      zIndex: 200000,
      backgroundColor: '#fff',
      '& .MuiOutlinedInput-root': {
        paddingRight: 0,
      },
    },
    pointsContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    pointButton: {
      minWidth: 0,
      width: '100%',
      justifyContent: 'flex-start',
      padding: '2rem 0',
    },
    dialog: {
      margin: 0,
      padding: 0,
      '& .MuiDialog-paper': {
        borderRadius: 32,
      },
    },
    zoomButtonsContainer: {
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      gap: '4px',
      bottom: 25,
      right: 21,
      zIndex: 1000,
    },
    zoomButton: {
      width: 40,
      height: 40,
      minWidth: 0,
      backgroundColor: '#fff',
      borderRadius: 8,
      border: 'solid 1px rgba(38, 32, 20, 0.1)',
      zIndex: 1000,
      '&:hover': {
        backgroundColor: '#ecebeb',
      },
    },
    closeButton: {
      width: 40,
      height: 40,
      minWidth: 0,
      backgroundColor: '#fff',
      borderRadius: 8,
      border: 'solid 1px rgba(38, 32, 20, 0.1)',
      position: 'absolute',
      right: 25,
      top: 25,
      zIndex: 1000,
      '&:hover': {
        backgroundColor: '#ecebeb',
      },
    },
  };
});

interface MapType {
  open: boolean;
  pickupPoints: any[];
  handleClose: () => void;
  handleSelectPickup: (pickup: any) => void;
  selectedPoint: any;
  loading: boolean;
}

const StyledListItemButton = styled(ListItem)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '16px 0',
  width: '100%',
  borderTop: '1px solid #dfdfdf',
  '&:first-of-type': {
    borderTop: 'none',
  },
  '&.Mui-selected': {
    backgroundColor: 'transparent',
    // color: '#ff6200',
    color: '#d01c1f',
    '& .MuiTypography-root': {
      color: '#d01c1f',
      opacity: 1,
    },
    '& .MuiListItemText-root:first-of-type .MuiTypography-root': {
      fontWeight: 700,
    },
    '&:hover': {
      // backgroundColor: 'rgba(255, 98, 0, 0.08)',
      backgroundColor: 'rgba(208, 28, 31, 0.08)',
    },
  },
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

const StyledListItemText = styled(ListItemText)({
  width: '100%',
  '& .MuiTypography-root': {
    fontSize: '1rem',
  },
});

const StyledListItemSubtitle = styled(ListItemText)({
  width: '100%',
  opacity: 0.6,
  '& .MuiTypography-root': {
    fontSize: '0.8rem',
  },
});

const GreenButton = styled(Button)(() => ({
  '&.MuiButton-root': {
    // backgroundColor: '#02401D !important',
    backgroundColor: '#ae171a !important',
    borderRadius: 8,
    padding: '10px 28px',
    fontWeight: 600,
    '&:hover': {
      // backgroundColor: '#013015 !important',
      backgroundColor: '#ae171a !important',
    },
  },
}));

const CustomMap = ({
  open = false,
  handleClose,
  selectedPoint,
  pickupPoints,
  handleSelectPickup,
  loading,
}: MapType) => {
  const [filterValue, setFilterValue] = useState<string>('');
  const classes = useStyles();
  const intl = useIntl();
  const [points, setPoints] = useState(pickupPoints);
  const [zoom, setZoom] = useState(10);
  const [myLocation, setMyLocation] = useState<any>([]);
  const mapRef = useRef<any>(null);
  const [selectLocation, setSelectLocation] = useState<any>({
    location: {
      latitude: selectedPoint?.latitude || 55.751574,
      longitude: selectedPoint?.longitude || 37.573856,
      address_full: selectedPoint?.address_full || '',
      address: selectedPoint?.address || '',
    },
  });

  useEffect(() => {
    if (pickupPoints?.length) {
      setPoints(pickupPoints);
    }
  }, [pickupPoints?.length]);

  useEffect(() => {
    if (selectedPoint?.city_code) {
      handlePointClick({
        location: {
          ...selectedPoint,
        },
      });
      setZoom(15);
    }
  }, []);

  useEffect(() => {
    if (open && !selectedPoint?.address_full) {
      const defaultPoint = points?.[0];
      if (defaultPoint) {
        handlePointClick(defaultPoint);
      }
    }
  }, [points.length, open]);

  const handlePointClick = (point: any) => {
    setSelectLocation(point);
    if (mapRef.current) {
      mapRef.current.setCenter([point.location.latitude, point.location.longitude], 15);
    }
  };

  const filter = useCallback(
    (point: any) => {
      const filteredPoints = pickupPoints?.filter((item: { location: { address: string } }) => {
        return item.location.address.toLowerCase().includes(point.target.value.toLowerCase());
      });

      if (filteredPoints.length) {
        handlePointClick(filteredPoints[0]);
      }
      return filteredPoints;
    },
    [pickupPoints]
  );

  const handleFilterChange = (e: any) => {
    setFilterValue(e.target.value);
    const filteredValue = filter(e);

    setPoints(filteredValue);
  };

  const handleGetLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        position => {
          if (mapRef.current) {
            mapRef.current.setCenter(
              [position.coords.latitude, position.coords.longitude],
              10
            );
            setMyLocation([position.coords.latitude, position.coords.longitude]);
            setSelectLocation({
              location: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              },
            });
          }
        },
        error => console.error('Error getting location:', error),
        { enableHighAccuracy: true }
      );
    } else {
      console.error('Geolocation is not supported by this browser.');
    }
  };

  const handleZoomIn = () => {
    if (zoom <= 19) {
      setZoom(prev => prev + 1);
    }
  };

  const handleZoomOut = () => {
    if (zoom > 3) {
      setZoom(prev => prev - 1);
    }
  };

  const handleSelect = async () => {
    await handleSelectPickup(selectLocation);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialog}
      fullWidth
      maxWidth='md'
    >
      <Box style={{ display: 'flex', justifyContent: 'space-between', overflow: 'hidden' }}>
        <Box style={{ padding: '28px 35px', flex: 1 }}>
          <Typography style={{
            fontSize: 24,
            fontWeight: 600,
            color: '#262014',
            marginBottom: 16
          }}>
            {intl.formatMessage({ id: 'STORE.SELECT' })}
          </Typography>
          <FilterField onChange={handleFilterChange} value={filterValue} />

          <Box className={classes.pointsContainer}>
            <List style={{ width: '100%', padding: 0, maxHeight: '500px', overflowY: 'auto' }}>
              {pickupPoints &&
                points?.map((point: any) => (
                  <StyledListItemButton
                    key={point?.address_full + point?.city_uuid}
                    button
                    onClick={() => {
                      setFilterValue(point.location.address);
                      handlePointClick(point);
                    }}
                    selected={
                      !!(point.location.address_full === selectLocation?.location?.address_full)
                    }
                  >
                    <StyledListItemText primary={point.name} />
                    <StyledListItemSubtitle primary={point.location.address_full} />
                  </StyledListItemButton>
                ))}
            </List>
          </Box>
        </Box>
        <Box style={{ flex: 2.2, position: 'relative', minHeight: '60vh' }}>
          <YMaps>
            <Map
              instanceRef={ref => {
                mapRef.current = ref;
              }}
              defaultState={{
                center: [55.751574, 37.573856],
                zoom,
              }}
              state={{
                center: [
                  selectLocation?.location?.latitude,
                  selectLocation?.location?.longitude,
                ],
                zoom,
              }}
              width='100%'
              height='100%'
            >
              <Button className={classes.closeButton} onClick={handleClose}>
                <CloseIcon style={{ color: '#26201440' }} />
              </Button>
              {pickupPoints
                ? pickupPoints?.map((point: any) => {
                    return (
                      <Placemark
                        key={point?.location?.id}
                        geometry={[point?.location?.latitude, point?.location?.longitude]}
                        onClick={() => {
                          setFilterValue(point.location.address);
                          handlePointClick(point);
                        }}
                        options={{
                          iconLayout: 'default#image',
                          // iconImageHref:
                          //   selectLocation?.location?.address === point?.location?.address
                          //     ? '/images/selectedLocation.png'
                          //     : '/images/pickupLocation.png',
                          iconImageHref: '/images/cdekMapSelect.svg',
                          // iconImageSize: [
                          //   selectLocation?.location?.address === point?.location?.address
                          //     ? 25
                          //     : 30,
                          //   40,
                          // ],
                          iconImageSize:
                            selectLocation?.location?.address === point?.location?.address
                              ? [50, 50]
                              : [36, 36],
                          // iconImageOffset: [-20, -40],
                          iconImageOffset:
                            selectLocation?.location?.address === point?.location?.address
                              ? [-25, -25]
                              : [-18, -18],
                        }}
                      />
                    );
                  })
                : null}
              {myLocation.length ? (
                <Placemark
                  geometry={myLocation}
                  options={{
                    iconLayout: 'default#image',
                    // iconImageHref: '/images/myLocation.png',
                    iconImageHref: '/images/myLocation.svg',
                    iconImageSize: [40, 40],
                    iconImageOffset: [-20, -40],
                  }}
                />
              ) : null}
              <div className={classes.zoomButtonsContainer}>
                <Button className={classes.zoomButton} onClick={handleGetLocation}>
                  {/* <img src='/images/myLocation.png' alt='location' width={20} height={20} /> */}
                  <img src='/images/myLocation.svg' alt='location' width={20} height={20} />
                </Button>
                <Button
                  className={classes.zoomButton}
                  onClick={handleZoomIn}
                  disabled={zoom > 19}
                >
                  <AddIcon />
                </Button>
                <Button
                  className={classes.zoomButton}
                  onClick={handleZoomOut}
                  disabled={zoom <= 3}
                >
                  <RemoveIcon />
                </Button>
              </div>
              <div
                style={{
                  position: 'absolute',
                  bottom: 20,
                  left: '50%',
                  translate: '-50%',
                  borderRadius: 5,
                  zIndex: 1000,
                }}
              >
                <GreenButton
                  onClick={handleSelect}
                  disabled={!selectLocation}
                  variant='contained'
                  color='primary'
                >
                  {intl.formatMessage({ id: 'MAP.SELECT_POINT' })}
                </GreenButton>
              </div>
            </Map>
          </YMaps>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CustomMap;
